import { useMemo, useState } from "react";
import { Modal, inputCls, btnTurq, btnGhost } from "../bits";
import { Plus, Trash2, Search, Users, CheckCircle2, FileDown } from "lucide-react";
import { API } from "../../lib/api";

const GRADES = ["أول ثانوي", "ثاني ثانوي", "ثالث ثانوي"];
const SECTIONS = ["١", "٢", "٣", "٤", "٥", "٦"];
const GRADE_PREFIX = { "أول ثانوي": "أولى", "ثاني ثانوي": "ثانية", "ثالث ثانوي": "ثالثة" };
const normalizeGrade = (g) => (g || "")
  .replace("الأول", "أول").replace("الثاني", "ثاني").replace("الثالث", "ثالث")
  .replace("الثانوي", "ثانوي").trim();

export function OppPartsModal({ open, opp, onClose, students, parts, partForm, setPartForm, busy, onAdd, onRemovePart, onBulkAdd, onUpdatePart, header, otherCount = 0 }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [grade, setGrade] = useState("");
  const [sec, setSec] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  const registered = useMemo(() => new Set(parts.map((p) => p.student_id)), [parts]);
  const gradeOf = useMemo(() => Object.fromEntries(students.map((s) => [s.id, s.grade])), [students]);
  const seats = opp?.seats || 0;
  const totalReg = parts.length + otherCount;
  const remaining = seats ? Math.max(0, seats - totalReg) : null;
  const seatsFull = seats > 0 && totalReg >= seats;
  const actualHours = useMemo(() => parts.reduce((a, p) => a + (Number(p.hours) || 0), 0), [parts]);

  const filtered = useMemo(
    () => students.filter((s) => (!grade || normalizeGrade(s.grade) === grade) && (!sec || s.section === sec) && (!q || s.name.includes(q))),
    [students, grade, sec, q]);
  const selectable = filtered.filter((s) => !registered.has(s.id));
  const allShownSelected = selectable.length > 0 && selectable.every((s) => selected.includes(s.id));

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAllShown = () => {
    if (allShownSelected) {
      setSelected((prev) => prev.filter((id) => !selectable.some((s) => s.id === id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...selectable.map((s) => s.id)])]);
    }
  };
  const submitBulk = async () => {
    await onBulkAdd(selected);
    setSelected([]);
  };

  return (
    <Modal open={open} onClose={onClose} title={`مشاركو «${opp?.name || ""}»`} wide testid="participants-modal">
      <div className="space-y-4">
        {header}
        <div data-testid="parts-stats" className="grid grid-cols-4 gap-2">
          <div className="rounded-xl bg-turq-100 dark:bg-surface2 p-2.5 text-center">
            <div data-testid="parts-registered" className="font-head font-black text-base text-turq-900 dark:text-turq-300">
              {parts.length}{seats ? ` / ${seats}` : ""}
            </div>
            <div className="text-[10px] text-mutext">المسجلون{seats ? " / المقاعد" : ""}</div>
          </div>
          <div className="rounded-xl bg-turq-100 dark:bg-surface2 p-2.5 text-center">
            <div data-testid="parts-completed" className="font-head font-black text-base text-turq-900 dark:text-turq-300">{parts.length}</div>
            <div className="text-[10px] text-mutext">منجزون</div>
          </div>
          <div className="rounded-xl bg-beige-300 dark:bg-surface2 p-2.5 text-center">
            <div data-testid="parts-actual-hours" className="font-head font-black text-base">{actualHours}</div>
            <div className="text-[10px] text-mutext">الساعات الفعلية</div>
          </div>
          <div className="rounded-xl bg-beige-300 dark:bg-surface2 p-2.5 text-center">
            <div data-testid="parts-opp-hours" className="font-head font-black text-base">{opp?.hours || 0}</div>
            <div className="text-[10px] text-mutext">ساعات الفرصة</div>
          </div>
        </div>

        {seatsFull && (
          <div data-testid="parts-seats-full" className="rounded-xl bg-yellow-100 dark:bg-surface2 border border-yellow-400/50 text-yellow-900 dark:text-yellow-300 text-[12px] font-bold px-3 py-2 text-center">
            اكتملت المقاعد ({seats}) — لا يمكن تسجيل مشاركين جدد
          </div>
        )}

        <div className="flex justify-end">
          <a data-testid="parts-export-btn" href={`${API}/portal/opportunities/${opp?.id}/participants/export`} target="_blank" rel="noreferrer"
            className={btnGhost}>
            <FileDown className="w-4 h-4" /> تصدير كشف الأسماء Excel
          </a>
        </div>

        <div className="flex gap-2 flex-wrap items-end bg-bg rounded-xl p-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] font-bold text-mutext block mb-1">الطالب</label>
            <select data-testid="participant-student-select" value={partForm.student_id}
              onChange={(e) => setPartForm({ ...partForm, student_id: e.target.value })} className={inputCls}>
              <option value="">— اختر طالباً —</option>
              {students.filter((s) => !registered.has(s.id)).map((s) => <option key={s.id} value={s.id}>{s.name} — {normalizeGrade(s.grade)}{s.section ? ` · ${GRADE_PREFIX[normalizeGrade(s.grade)] || ""} ${s.section}` : ""}</option>)}
            </select>
          </div>
          <div className="w-24">
            <label className="text-[11px] font-bold text-mutext block mb-1">الساعات</label>
            <input data-testid="participant-hours-input" type="number" min="1" value={partForm.hours}
              onChange={(e) => setPartForm({ ...partForm, hours: Number(e.target.value) })} className={inputCls} />
          </div>
          <button data-testid="participant-add-btn" onClick={onAdd} disabled={busy || seatsFull} className={btnTurq}>
            <Plus className="w-4 h-4" /> تسجيل
          </button>
          <button data-testid="bulk-picker-toggle" onClick={() => setPickerOpen((v) => !v)} disabled={seatsFull} className={btnGhost}>
            <Users className="w-4 h-4" /> اختيار جماعي
          </button>
        </div>

        {pickerOpen && !seatsFull && (
          <div data-testid="bulk-picker" className="rounded-2xl border border-turq-300/50 bg-turq-100/40 dark:bg-surface2 p-3 space-y-3 animate-fade-up">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <b className="font-head text-[13px] text-turq-900 dark:text-turq-300">
                اختر الطلاب المشاركين{remaining !== null ? ` (المتبقي من المقاعد: ${remaining})` : ""}
              </b>
              <button data-testid="bulk-add-btn" onClick={submitBulk} disabled={busy || selected.length === 0} className={btnTurq}>
                إضافة المحدد ({selected.length})
              </button>
            </div>
            <div className="flex gap-1.5 flex-wrap" data-testid="bulk-grade-chips">
              {["", ...GRADES].map((g) => (
                <button key={g || "all"} data-testid={`bulk-grade-chip-${g || "all"}`} onClick={() => { setGrade(g); if (!g) setSec(""); }}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition-all ${grade === g
                    ? "bg-coral-600 text-white shadow-md"
                    : "bg-surface text-mutext border border-brd hover:scale-105"}`}>
                  {g || "الكل"} ({students.filter((s) => !g || normalizeGrade(s.grade) === g).length})
                </button>
              ))}
            </div>
            {grade && (
              <div className="flex gap-1.5 flex-wrap" data-testid="bulk-section-chips">
                {["", ...SECTIONS].map((sc) => (
                  <button key={sc || "all"} data-testid={`bulk-section-chip-${sc || "all"}`} onClick={() => setSec(sc)}
                    className={`rounded-full px-3 py-1 text-[11px] font-bold transition-all ${sec === sc
                      ? "bg-turq-600 text-white shadow-md"
                      : "bg-surface text-mutext border border-brd hover:scale-105"}`}>
                    {sc ? `${GRADE_PREFIX[grade] || grade} ${sc}` : "كل الشُعب"} ({students.filter((s) => normalizeGrade(s.grade) === grade && (!sc || s.section === sc)).length})
                  </button>
                ))}
              </div>
            )}
            <div className="relative">
              <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-mutext pointer-events-none" />
              <input data-testid="bulk-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="ابحث عن طالب بالاسم أو الصف…" className={`${inputCls} pr-9`} />
            </div>
            <div className="flex items-center justify-between gap-2">
              <button data-testid="bulk-select-all" onClick={toggleAllShown} disabled={selectable.length === 0}
                className="text-[11px] font-bold bg-turq-100 dark:bg-surface border border-turq-300/50 text-turq-900 dark:text-turq-300 rounded-full px-3 py-1.5 hover:bg-turq-300/50 transition-colors">
                {allShownSelected ? `إلغاء تحديد المعروض (${selectable.length})` : `تحديد الكل المعروض (${selectable.length})`}
              </button>
              <span data-testid="bulk-selected-count" className="text-[11px] font-bold text-mutext">المحدد: {selected.length}</span>
            </div>
            <div className="max-h-72 overflow-auto space-y-1.5 pl-1">
              {filtered.map((s) => {
                const isReg = registered.has(s.id);
                const isSel = selected.includes(s.id);
                return (
                  <label key={s.id} data-testid={`bulk-student-${s.id.slice(0, 8)}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-[13px] transition-colors ${isReg
                      ? "opacity-45 cursor-not-allowed bg-bg border-brd"
                      : isSel
                        ? "bg-turq-100 dark:bg-surface border-turq-600 cursor-pointer"
                        : "bg-surface border-brd hover:border-turq-300 cursor-pointer"}`}>
                    <input type="checkbox" checked={isSel} disabled={isReg} onChange={() => toggle(s.id)}
                      className="w-4 h-4 accent-[#12939E]" />
                    <span className="flex-1 font-bold">{s.name}</span>
                    <span className="text-[10px] text-mutext">{normalizeGrade(s.grade)}{s.section ? ` · ${GRADE_PREFIX[normalizeGrade(s.grade)] || "شعبة"} ${s.section}` : ""}{isReg ? " · مسجل مسبقاً" : ""}</span>
                  </label>
                );
              })}
              {filtered.length === 0 && <div className="text-center text-mutext text-xs py-6">لا يوجد طلاب مطابقون</div>}
            </div>
          </div>
        )}

        <div className="max-h-64 overflow-auto rounded-xl border border-brd">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-turq-900 text-white">
                <th className="text-right px-3 py-2">الطالب</th>
                <th className="text-right px-3 py-2">الصف</th>
                <th className="text-right px-3 py-2">منجز</th>
                <th className="text-right px-3 py-2">الساعات</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id} data-testid={`participant-row-${p.id.slice(0, 8)}`} className="border-t border-brd/50">
                  <td className="px-3 py-2 font-bold">{p.student_name}</td>
                  <td className="px-3 py-2 text-mutext">{gradeOf[p.student_id] || "—"}</td>
                  <td className="px-3 py-2">
                    <span data-testid={`participant-done-${p.id.slice(0, 8)}`} className="inline-flex items-center gap-1 rounded-full bg-turq-600 text-white text-[10px] font-bold px-2.5 py-1">
                      <CheckCircle2 className="w-3 h-3" /> منجز
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <input data-testid={`participant-hours-edit-${p.id.slice(0, 8)}`} type="number" min="1" max="1000"
                      defaultValue={p.hours} key={`${p.id}-${p.hours}`}
                      onBlur={(e) => { const v = Number(e.target.value); if (v > 0 && v !== p.hours) onUpdatePart(p.id, v, p.student_id); }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="w-20 rounded-lg border border-brd bg-bg px-2 py-1 text-center font-bold" dir="ltr" />
                  </td>
                  <td className="px-3 py-2 text-left">
                    <button data-testid={`participant-delete-${p.id.slice(0, 8)}`} onClick={() => onRemovePart(p.id)}
                      className="text-coral-900 dark:text-coral-300 hover:scale-110 transition-transform">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {parts.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-mutext">لا يوجد مشاركون بعد</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-mutext">تذكير: الطالب المشارك في عدة فرص يُحتسب «طالباً فريداً واحداً» في مؤشر التمكين 90%.</p>
      </div>
    </Modal>
  );
}
